import {
  ELEVATED_CUT_STEP_RATIO,
  RECOVERY_HARD_STANDING_QUEUE_MS_EXIT,
  RECOVERY_STANDING_QUEUE_MS_EXIT,
  TARGET_FRAMERATE,
  hardCutBitrateBpsFor,
  recoveryClimbCeilingBps,
  stickyFloorEncoding,
} from '@playcastdotio/PeerCore';
import type { SessionDowngradeFloor, StickyFloorEncoding, StreamEncodeContext } from '@playcastdotio/PeerCore';
import type { PeerRecovery, RecoveryCutKind } from './RecoveryEpisodeStore';

/**
 * RecoveryCutPolicy — the cut arithmetic, pure.
 *
 * Every bitrate the recovery manager applies is computed here from an explicit context, so
 * the numbers can be tested without a manager, and the two ways into the same cut (a fresh
 * entry and an escalation) cannot disagree about what the cut is.
 *
 * The `elevated` cut is a STEP, not a plunge: `max(current × ELEVATED_CUT_STEP_RATIO, hard
 * cut)`. Field data (Android, 2026-09-19): the previous quality-based soft cut took an 8 000
 * kbps cap to 1 806 kbps (−77 %) on the second guest sample after connect, while
 * glass-to-glass was at its best of the run. A soft cut is meant to be a nudge.
 */

/** What a cut is computed against for one peer. */
export type RecoveryCutContext = {
  /** Operator / profile ceiling for the peer, bps. */
  profileMaxBitrateBps: number;
  /** Live capture + codec, when known; the hard cut is a picture-quality floor under it. */
  encodeContext?: StreamEncodeContext;
  /** The peer's effective fps ceiling (sticky ceiling, never above the profile max). */
  fpsCeiling: number;
};

/** The climb reaches its ceiling in this many equal steps. */
export const CLIMB_RAMP_STEPS = 3;

/**
 * Hard-cut bitrate for this peer, bounded by the operator ceiling. Both hard-entry paths go
 * through here: when they each clamped for themselves, only one learned about the ceiling.
 */
export function hardCutBitrateFor(ctx: RecoveryCutContext): number {
  return hardCutBitrateBpsFor(ctx.profileMaxBitrateBps, ctx.encodeContext);
}

/**
 * The `elevated` step: three quarters of the cap the peer currently has, never below the
 * hard cut and never above what it already was. A cap that is already at or under the hard
 * cut is left where it is — a step below the emergency floor is not a softer cut.
 */
export function stepCutBitrateFor(currentMaxBitrateBps: number, ctx: RecoveryCutContext): number {
  const current =
    Number.isFinite(currentMaxBitrateBps) && currentMaxBitrateBps > 0
      ? currentMaxBitrateBps
      : ctx.profileMaxBitrateBps;
  const stepped = Math.max(1, Math.round(current * ELEVATED_CUT_STEP_RATIO));
  return Math.min(current, Math.max(stepped, hardCutBitrateFor(ctx)));
}

/** The level a `soft` sticky floor holds at: one step below the profile ceiling. */
export function softFloorBitrateFor(ctx: RecoveryCutContext): number {
  return stepCutBitrateFor(ctx.profileMaxBitrateBps, ctx);
}

/** The bitrate a sticky floor pins the peer at; undefined for `none`. */
export function floorBitrateFor(floor: SessionDowngradeFloor, ctx: RecoveryCutContext): number | undefined {
  if (floor === 'none') return undefined;
  if (floor === 'soft') return softFloorBitrateFor(ctx);
  // 'hard' and 'tier360' both hold at the hard-cut bitrate.
  return hardCutBitrateFor(ctx);
}

/**
 * The climb ceiling for a peer under a sticky floor (PRODUCT DECISION D1). `none` returns
 * the picture ceiling; a floored peer climbs to its floor's own level and holds there. The
 * bitrate (not resolution) is what the floor pins; the `tier360` resolution change itself is
 * applied by the decode-lag path, not here.
 */
export function climbCeilingFor(floor: SessionDowngradeFloor, ctx: RecoveryCutContext): number {
  // Ramp toward what the picture can actually use, not the raw profile ceiling: at 360p that
  // ceiling is roughly four times what the capture can spend, and handing it back to a link
  // that was congested seconds ago is how a recovered stream re-congests itself.
  const pictureCeiling = recoveryClimbCeilingBps(ctx.profileMaxBitrateBps, ctx.encodeContext);
  const floorBps = floorBitrateFor(floor, ctx);
  return floorBps === undefined ? pictureCeiling : Math.min(pictureCeiling, floorBps);
}

/**
 * Encoding to prime a new or reclaimed connection under a sticky floor. Never above the
 * floor's level, and never above an open episode's own cap when there is one.
 */
export function stickyFloorEncodingFor(
  floor: SessionDowngradeFloor,
  ctx: RecoveryCutContext,
  openEpisodeMaxBitrateBps?: number,
): StickyFloorEncoding {
  let cutBitrateBps = floorBitrateFor(floor, ctx);
  if (openEpisodeMaxBitrateBps !== undefined && openEpisodeMaxBitrateBps > 0) {
    cutBitrateBps =
      cutBitrateBps !== undefined
        ? Math.min(cutBitrateBps, openEpisodeMaxBitrateBps)
        : openEpisodeMaxBitrateBps;
  }
  return stickyFloorEncoding(floor, ctx.profileMaxBitrateBps, {
    profileMaxFps: TARGET_FRAMERATE,
    fpsCeiling: ctx.fpsCeiling,
    cutBitrateBps,
  });
}

/** A fresh `elevated` (soft) episode: one step below the cap the peer currently has. */
export function softEpisode(currentMaxBitrateBps: number, ctx: RecoveryCutContext, nowMs: number): PeerRecovery {
  const bitrate = stepCutBitrateFor(currentMaxBitrateBps, ctx);
  return episodeAt('soft', bitrate, ctx, nowMs);
}

/** A fresh `critical` (hard) episode at the peer's hard-cut bitrate. */
export function hardEpisode(ctx: RecoveryCutContext, nowMs: number): PeerRecovery {
  return episodeAt('hard', hardCutBitrateFor(ctx), ctx, nowMs);
}

/**
 * Escalate an open soft episode to the hard cut, in place. Escalation must never raise the
 * cap, so it cannot exceed the soft cut it is escalating from either — the ceiling can move
 * mid-episode. The escalate clock restarts from this moment.
 */
export function escalateToHard(existing: PeerRecovery, ctx: RecoveryCutContext, nowMs: number): void {
  const bitrate = Math.min(hardCutBitrateFor(ctx), existing.maxBitrateBps);
  existing.episodeCutKind = 'hard';
  existing.maxBitrateBps = bitrate;
  existing.maxFramerate = Math.min(TARGET_FRAMERATE, ctx.fpsCeiling);
  existing.cutFloorBitrateBps = bitrate;
  existing.enteredAt = nowMs;
  existing.healthySince = null;
  existing.rampStep = 0;
}

/** A restored hold at a sticky floor after a connection rebuild: recovering, already healthy. */
export function restoredFloorEpisode(floor: SessionDowngradeFloor, encoding: StickyFloorEncoding, nowMs: number): PeerRecovery {
  const peer = episodeAt(floor === 'soft' ? 'soft' : 'hard', encoding.maxBitrateBps, undefined, nowMs);
  peer.maxFramerate = encoding.maxFramerate;
  peer.healthySince = nowMs;
  return peer;
}

/**
 * The exit gate follows the cut the episode ENTERED with, never a de-escalated reported
 * kind: letting the climb tighten its own exit threshold mid-ramp would stall it in the dead
 * band between the two thresholds.
 */
export function exitQueueThresholdFor(cutKind: RecoveryCutKind): number {
  return cutKind === 'hard' ? RECOVERY_HARD_STANDING_QUEUE_MS_EXIT : RECOVERY_STANDING_QUEUE_MS_EXIT;
}

/** Bitrate for ramp step `step` of CLIMB_RAMP_STEPS from `floorBps` toward `ceilingBps`. */
export function rampBitrateFor(floorBps: number, ceilingBps: number, step: number): number {
  return Math.min(ceilingBps, floorBps + Math.round(((ceilingBps - floorBps) * step) / CLIMB_RAMP_STEPS));
}

function episodeAt(
  cutKind: RecoveryCutKind,
  maxBitrateBps: number,
  ctx: RecoveryCutContext | undefined,
  nowMs: number,
): PeerRecovery {
  return {
    state: 'recovering',
    episodeCutKind: cutKind,
    enteredAt: nowMs,
    healthySince: null,
    keyframeSent: false,
    maxBitrateBps,
    maxFramerate: ctx ? Math.min(TARGET_FRAMERATE, ctx.fpsCeiling) : TARGET_FRAMERATE,
    rampStep: 0,
    cutFloorBitrateBps: maxBitrateBps,
    sustainedQueueSince: null,
  };
}
